import { LoanOwner } from '~/Interfaces';
import React, { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '~/store/types/sharedTypes';
import useTranslate from '~/hooks/useTranslate';
import * as offerActions from '~/store/actions/loanOfferActions';
import CardSection from '~/components/shared/CardSection/CardSection';
import InputField from '~/components/shared/Input/InputField';
import {
  getDatePlaceholder,
  getPersonalNumberPlaceholder,
} from '~/constants/markets';
import Button, { ButtonType } from '~/components/shared/Button/button';
import { ValidationContext } from '~/components/hoc/withValidation';
import { CountryCode, OwnerTypeEnum } from '~/enums';
import moment from 'moment';
import DatePicker from '~/components/shared/DatePicker/DatePicker';
import ValidationErrorMessage from '~/components/shared/ValidationErrorMessage/ValidationErrorMessage';

interface AdditionalOwnerProps {
  owner: LoanOwner;
  index: number;
  setNewPersonIsAllowed: (value: boolean) => void;
}

export const AdditionalOwner: React.FC<AdditionalOwnerProps> = (props) => {
  const { owner, index, setNewPersonIsAllowed } = props;

  const { market, language } = useSelector((state: RootState) => state.intl);
  const { owners: beneficialOwners, ownerType } = useSelector(
    (state: RootState) => state.loanOffer.userData
  );
  const t = useTranslate();
  const dispatch = useDispatch();
  const validationContext = useContext(ValidationContext);
  const validationErrorOwnerAlreadyExistsShouldBeShown =
    validationContext.validationErrors[`OwnerAlreadyExists_${index}`];
  const validationErrorOwnerMoreThan25ShouldBeShown =
    validationContext.validationErrors[`OwnerMoreThan25_${index}`];

  const ownerPercentageHandler = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    dispatch(
      offerActions.updateOfferOwner({
        ...owner,
        percentage: event.currentTarget.value,
      })
    );
  };

  const ownerPercentageOnBlurHandler = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event?.currentTarget?.value;
    if (value)
      dispatch(
        offerActions.updateOfferOwner({
          ...owner,
          percentage: value.replace(/\%/g, ''),
        })
      );
  };

  const ownerPersonalNumberHandler = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    dispatch(
      offerActions.updateOfferOwner({
        ...owner,
        personalNumber: event.currentTarget.value,
      })
    );
  };

  const ownerDateOfBirthHandler = (date: Date | null) => {
    if (date instanceof Date) {
      dispatch(
        offerActions.updateOfferOwner({
          ...owner,
          personalNumber: moment(date).format('YYYY-MM-DD'),
        })
      );
    }
  };

  const ownerNameHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(
      offerActions.updateOfferOwner({
        ...owner,
        name: event.currentTarget.value,
      })
    );
  };

  const removeOwner = (id: string) => {
    if (beneficialOwners.length > 0) {
      dispatch(offerActions.removeOwnerPlaceholder(id));
    }
    setNewPersonIsAllowed(true);
  };

  useEffect(() => {
    if (
      ownerType === OwnerTypeEnum.NonOneOwn25 &&
      Number(owner.percentage) > 25
    ) {
      validationContext.addPropertyToValidationErrors(
        `OwnerMoreThan25_${index}`
      );
    }
    return () => {
      validationContext.removePropertyFromValidationErrors(
        `OwnerMoreThan25_${index}`
      );
    };
  }, [owner.percentage]);

  useEffect(() => {
    if (
      market !== CountryCode.NL &&
      beneficialOwners
        .filter((o) => o.id !== owner.id)
        .some((o) => o.personalNumber === owner.personalNumber)
    ) {
      validationContext.addPropertyToValidationErrors(
        `OwnerAlreadyExists_${index}`
      );
    }

    return () => {
      validationContext.removePropertyFromValidationErrors(
        `OwnerAlreadyExists_${index}`
      );
    };
  }, [owner.personalNumber]);

  return (
    <CardSection
      title={`${t('CompanyOwnersCard.AdditionalOwner')} #${Number(index + 1)}`}
      borderBottom
    >
      <InputField
        name={`fullNameOwner${index}`}
        value={owner.name}
        validationType="FirstAndLastNameOwner"
        onChange={ownerNameHandler}
        placeholder={t('FullName') as string}
      />
      {market === CountryCode.NL ? (
        <DatePicker
          name={`dateOfBirthOwner${index}`}
          validationType="Required"
          locale={language}
          maxDate={new Date()}
          placeholder={getDatePlaceholder(market, language)}
          onChange={ownerDateOfBirthHandler}
        />
      ) : (
        <>
          <InputField
            name={`personalNumberOwner${index}`}
            value={owner.personalNumber}
            validationType="PersonalNumber"
            onChange={ownerPersonalNumberHandler}
            placeholder={getPersonalNumberPlaceholder(market, language)}
          />
        </>
      )}
      {validationErrorOwnerAlreadyExistsShouldBeShown && (
        <ValidationErrorMessage>
          {t('ValidationErrors.OwnerAlreadyExists')}
        </ValidationErrorMessage>
      )}
      <InputField
        name={`percentageShare${index}`}
        value={owner.percentage}
        validationType="PercentageShareFormat"
        onChange={ownerPercentageHandler}
        onBlur={ownerPercentageOnBlurHandler}
        placeholder={
          t('CompanyOwnersCard.PercentageSharePlaceholder') as string
        }
      />
      {validationErrorOwnerMoreThan25ShouldBeShown && (
        <ValidationErrorMessage>
          {t('ValidationErrors.OwnerMoreThan25')}
        </ValidationErrorMessage>
      )}
      <Button
        mt={1}
        type={ButtonType.Outlined}
        onClick={() => removeOwner(owner.id)}
      >
        {t('LoanInfoCard.RemovePerson') as string}
      </Button>
    </CardSection>
  );
};
